import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, Quote, Star, ShieldCheck, Mail, Phone } from "lucide-react";
import Seo from "../components/Seo";
import { directors, findDirector } from "../data/directors";

const accentText = {
  magenta: "text-brand-primary",
  wine: "text-brand-wine",
  gold: "text-[#B97900]",
};

const accentFrame = {
  magenta:
    "ring-[#E91E63]/35 shadow-[0_50px_120px_-30px_rgba(233,30,99,0.55)] before:bg-[radial-gradient(circle_at_30%_30%,rgba(233,30,99,0.35),transparent_60%)]",
  wine:
    "ring-[#A02157]/35 shadow-[0_50px_120px_-30px_rgba(160,33,87,0.55)] before:bg-[radial-gradient(circle_at_30%_30%,rgba(160,33,87,0.35),transparent_60%)]",
  gold:
    "ring-[#FFB72E]/45 shadow-[0_50px_120px_-30px_rgba(255,183,46,0.55)] before:bg-[radial-gradient(circle_at_30%_30%,rgba(255,183,46,0.35),transparent_60%)]",
};

export default function DirectorDetail() {
  const { slug } = useParams();
  const director = findDirector(slug);

  if (!director) return <Navigate to="/team" replace />;

  const otherDirectors = directors.filter((d) => d.slug !== slug).slice(0, 3);

  return (
    <div className="relative overflow-hidden" data-testid={`director-page-${director.slug}`}>
      <Seo
        title={`${director.name} — ${director.title} | Dkarez Community Support CIC`}
        description={director.short}
        image={director.photo}
      />

      {/* Ambient glows */}
      <div className="gold-glow bg-brand-primary-light w-[600px] h-[600px] -top-48 -left-40" />
      <div className="gold-glow bg-brand-gold-light w-[520px] h-[520px] top-1/3 -right-40" />

      {/* Back link */}
      <div className="relative max-w-6xl mx-auto px-4 sm:px-8 pt-10">
        <Link
          to="/team"
          className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-primary transition-colors"
          data-testid="director-back-link"
        >
          <ArrowLeft size={16} strokeWidth={1.8} />
          Back to our leadership
        </Link>
      </div>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-8 pt-10 pb-16 sm:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Portrait */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.65 }}
          className="lg:col-span-5 depth-3d"
        >
          <div
            className={`relative rounded-[36px] overflow-hidden ring-1 ring-inset float-3d
              before:content-[''] before:absolute before:inset-0 before:opacity-90 before:pointer-events-none before:rounded-[36px]
              ${accentFrame[director.accent]}`}
            style={{ transform: "rotateX(2deg) rotateY(-3deg)" }}
          >
            <img
              src={director.photo}
              alt={director.name}
              className="relative w-full aspect-[4/5] object-cover"
            />
            {/* Gold corner star */}
            <div className="absolute top-5 right-5 w-12 h-12 rounded-full bg-brand-gold-light backdrop-blur-md flex items-center justify-center ring-1 ring-brand-gold/40 shadow-lg">
              <Star size={18} strokeWidth={1.6} fill="currentColor" className="text-brand-gold" />
            </div>
            {/* Bottom name plate */}
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[rgba(42,14,31,0.85)] via-[rgba(42,14,31,0.4)] to-transparent">
              <span className="font-script text-3xl text-white/95 drop-shadow">
                {director.title.split("&")[0].trim().toLowerCase()}
              </span>
            </div>
          </div>

          {/* Pillars */}
          <div className="mt-6 flex flex-wrap gap-2">
            {director.pillars.map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-brand text-xs font-semibold text-brand-wine"
              >
                <ShieldCheck size={12} strokeWidth={2} className={accentText[director.accent]} />
                {p}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <span className="eyebrow" data-testid="director-eyebrow">
            {director.title}
          </span>
          <h1 className="font-display-bold text-4xl sm:text-5xl lg:text-[3.4rem] mt-4 leading-[1.05]">
            {director.name.replace(/^(Mr|Mrs|Ms)\s/, "")}{" "}
            <span className="font-script text-brand-primary text-[1.1em] block sm:inline align-baseline mt-2 sm:mt-0">
              &amp; the Dkarez story
            </span>
          </h1>
          <p className="mt-6 text-lg text-brand-muted leading-relaxed max-w-2xl">
            {director.short}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/campaigns" className="btn-gold" data-testid="director-cta-donate">
              <Heart size={15} className="mr-2" strokeWidth={1.8} fill="currentColor" />
              Donate in support
            </Link>
            <Link to="/contact" className="btn-outline-gold" data-testid="director-cta-contact">
              Get in touch
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Long-form bio */}
      <section className="relative max-w-4xl mx-auto px-4 sm:px-8 pb-20">
        <div className="glass-card rounded-[28px] p-8 sm:p-12 depth-shadow">
          {director.paragraphs.map((p, idx) => (
            <p
              key={`${director.slug}-p-${idx}`}
              className="text-[1.05rem] sm:text-[1.1rem] leading-[1.85] text-brand-text/90 mb-6 last:mb-0"
            >
              {idx === 0 ? (
                <>
                  <span className="float-left font-display-bold text-6xl sm:text-7xl leading-[0.85] mr-3 mt-1 text-brand-primary">
                    {p.charAt(0)}
                  </span>
                  {p.slice(1)}
                </>
              ) : (
                p
              )}
            </p>
          ))}
        </div>
      </section>

      {/* Contact card — only when director has contact details */}
      {director.contacts && (
        <section className="relative max-w-4xl mx-auto px-4 sm:px-8 pb-16">
          <div className="glass-card rounded-[24px] p-7 sm:p-9 depth-shadow">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <span className="eyebrow" data-testid="director-contact-eyebrow">Direct Contact</span>
                <h3 className="font-display-bold text-2xl sm:text-3xl mt-2">
                  Reach out to {director.name.split(" ").slice(0, 2).join(" ")}
                </h3>
                <p className="mt-2 text-brand-muted text-[0.95rem] max-w-xl">
                  For partnership enquiries, safeguarding matters or community support — feel
                  free to get in touch directly.
                </p>
              </div>
              <div className="flex flex-col gap-3 shrink-0">
                {director.contacts.email && (
                  <a
                    href={`mailto:${director.contacts.email}`}
                    data-testid={`director-email-${director.slug}`}
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-brand-primary-light hover:bg-brand-pink-hover transition-colors text-brand-wine font-medium group"
                  >
                    <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm">
                      <Mail size={16} strokeWidth={2} className="text-brand-primary" />
                    </span>
                    <span className="text-sm sm:text-[0.95rem] break-all">
                      {director.contacts.email}
                    </span>
                  </a>
                )}
                {director.contacts.phone && (
                  <a
                    href={`tel:${director.contacts.phone.replace(/\s/g, "")}`}
                    data-testid={`director-phone-${director.slug}`}
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-brand-gold-light hover:bg-[#FFE9B0] transition-colors text-brand-wine-deep font-medium"
                  >
                    <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm">
                      <Phone size={16} strokeWidth={2} className="text-[#B97900]" />
                    </span>
                    <span className="text-sm sm:text-[0.95rem]">
                      {director.contacts.phone}
                    </span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Vision card */}
      <section className="relative max-w-4xl mx-auto px-4 sm:px-8 pb-24">
        <div className="relative rounded-[28px] p-10 sm:p-14 overflow-hidden bg-gradient-to-br from-[#3A0F26] via-brand-wine to-brand-primary text-white depth-shadow-magenta">
          <Quote
            size={64}
            strokeWidth={1}
            className="absolute top-6 left-6 text-white/15"
          />
          <div className="relative">
            <span className="font-script text-4xl sm:text-5xl text-brand-gold">
              {director.name.startsWith("Mrs") ? "Her vision" : "His vision"}
            </span>
            <p className="font-display-bold text-2xl sm:text-3xl mt-3 leading-snug max-w-3xl">
              "{director.vision}"
            </p>
            <div className="mt-6 text-sm tracking-wider uppercase text-white/70">
              — {director.name}
            </div>
          </div>
        </div>
      </section>

      {/* Meet other directors */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-8 pb-28">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="eyebrow">Leadership team</span>
            <h2 className="font-display-bold text-3xl sm:text-4xl mt-3">
              Meet the rest of our directors
            </h2>
          </div>
          <Link
            to="/team"
            className="hidden sm:inline-flex text-sm font-medium text-brand-primary hover:text-brand-wine"
          >
            View all →
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {otherDirectors.map((d) => (
            <Link
              key={d.slug}
              to={`/team/${d.slug}`}
              data-testid={`director-related-${d.slug}`}
              className="group premium-card overflow-hidden flex flex-col"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={d.photo}
                  alt={d.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <span className="text-[0.65rem] uppercase tracking-[0.18em] font-semibold text-brand-primary">
                  {d.title}
                </span>
                <h3 className="font-display-bold text-lg mt-1 text-brand-text">
                  {d.name}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
